import React, { useState } from 'react';
import styled from 'styled-components';
import Window from '../Window';
import { User, Code, Image, FileText } from 'lucide-react';

const AboutContainer = styled.div`
  display: flex;
  height: 100%;
  background: #f5f5f7;
`;

const Sidebar = styled.div`
  width: 200px;
  background: rgba(255, 255, 255, 0.8);
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  padding: 16px 0;
`;

const SidebarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 122, 255, 0.1);
  }
  
  &.active {
    background: rgba(0, 122, 255, 0.2);
    color: #007AFF;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TabBar = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  
  &:hover {
    color: #333;
  }
  
  &.active {
    color: #007AFF;
    border-bottom-color: #007AFF;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
`;

const NotepadContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: pre-wrap;
`;

const ImagePreview = styled.div`
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  margin: 20px 0;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const SkillCard = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  h4 {
    margin: 0 0 8px 0;
    color: #007AFF;
    font-size: 14px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 12px;
    color: #666;
  }
`;

const AboutMeApp = ({ ...windowProps }) => {
  const [activeSidebar, setActiveSidebar] = useState('about');
  const [activeTab, setActiveTab] = useState('about');

  const aboutText = `Hi! I'm Aviral Malik, a passionate Software Developer with expertise in full-stack development and emerging technologies. I'm currently pursuing my Bachelor of Technology in Information Technology at Manipal Institute of Technology, Manipal.

With a strong foundation in programming languages like Python, C#, Java, and JavaScript, I specialize in building scalable web applications and microservices. My experience includes developing real-time chat applications, full-stack platforms, and optimizing database performance.

I've worked as a Software Developer Intern at NDTV, where I developed microservices and improved system efficiency by 50%. I'm also experienced with cloud technologies like AWS, Docker, and CI/CD pipelines.

My goal is to create innovative solutions that solve real-world problems while continuously learning and adapting to new technologies.`;

  const skills = [
    { category: 'Programming Languages', skills: 'Python, C#, C++, Java, JavaScript, HTML/CSS, SQL' },
    { category: 'Frameworks & Libraries', skills: 'Django, Flask, ASP.NET, Bootstrap, Tailwind, Numpy, Pandas' },
    { category: 'Cloud & DevOps', skills: 'AWS EC2, Docker, Git, GitHub, CI/CD pipelines' },
    { category: 'Databases', skills: 'MongoDB, InfluxDB, SQL' },
    { category: 'Tools', skills: 'Postman, Android Studio, RapidMiner, Blender, Figma, Adobe Suite' }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'about':
        return <NotepadContent>{aboutText}</NotepadContent>;
      case 'skills':
        return (
          <div>
            <h3>Skills & Expertise</h3>
            <SkillsGrid>
              {skills.map((skill, index) => (
                <SkillCard key={index}>
                  <h4>{skill.category}</h4>
                  <p>{skill.skills}</p>
                </SkillCard>
              ))}
            </SkillsGrid>
          </div>
        );
      case 'photo':
        return (
          <div>
            <h3>Profile Photo</h3>
            <ImagePreview>👨‍💻</ImagePreview>
            <p style={{ textAlign: 'center', color: '#666' }}>
              Professional headshot coming soon...
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Window {...windowProps} size={{ width: 700, height: 500 }}>
      <AboutContainer>
        <Sidebar>
          <SidebarItem 
            className={activeSidebar === 'about' ? 'active' : ''} 
            onClick={() => setActiveSidebar('about')}
          >
            <User size={16} />
            About Me
          </SidebarItem>
          <SidebarItem 
            className={activeSidebar === 'skills' ? 'active' : ''} 
            onClick={() => setActiveSidebar('skills')}
          >
            <Code size={16} />
            Skills
          </SidebarItem>
          <SidebarItem 
            className={activeSidebar === 'photo' ? 'active' : ''} 
            onClick={() => setActiveSidebar('photo')}
          >
            <Image size={16} />
            Photo
          </SidebarItem>
        </Sidebar>
        <MainContent>
          <TabBar>
            <Tab 
              active={activeTab === 'about'} 
              onClick={() => setActiveTab('about')}
            >
              <FileText size={14} style={{ marginRight: '8px' }} />
              About.txt
            </Tab>
            <Tab 
              active={activeTab === 'skills'} 
              onClick={() => setActiveTab('skills')}
            >
              <Code size={14} style={{ marginRight: '8px' }} />
              Skills.txt
            </Tab>
            <Tab 
              active={activeTab === 'photo'} 
              onClick={() => setActiveTab('photo')}
            >
              <Image size={14} style={{ marginRight: '8px' }} />
              Photo.jpg
            </Tab>
          </TabBar>
          <ContentArea>{renderContent()}</ContentArea>
        </MainContent>
      </AboutContainer>
    </Window>
  );
};

export default AboutMeApp;
